import { Button, Drawer, Space } from 'antd';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { basicConfig, fitScreenView, formatNodesAndEdges } from './basicConfig';
import { useSize } from 'ahooks';
import styles from './index.module.scss';
import G6, { Graph } from '@antv/g6';
import { FormTitle, useBreadcrumbRoutes, useTheme, Wrapper } from '@maxtropy/components';
import ItemProcessDetail from './components/ItemProcessDetail';
import { EnergyWorkingProcessType, EnergyWorkingProcessTypeLabel, EnergyWorkingProcessTypeLabelColor } from '@/api/uet';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import {
  V2UetEmtPreviewDashboardAuthorityPostResponse,
  apiV2UetEmtPreviewDashboardAuthorityPost,
} from '@maxtropy/device-customer-apis-v2';

const routes = [{ name: '预览视图' }];

const ViewEnergyWorkingTopo: FC = () => {
  const { energyId } = useParams<{ id: string; energyId: string }>();
  const theme = useTheme();
  const [open, setOpen] = useState(false); // 抽屉的弹窗
  const ref = useRef<HTMLDivElement>(); // ref考虑自适应问题
  const [item, setItem] = useState<any>(); // 文档与版本脱离，所以用any 目标节点的实例
  const [itemDetailId, setItemDetailId] = useState<number>(); // 每个节点的详情id
  const graph = useRef<Graph>(); // graph图的实例
  const [wheelzoomPercent, setWheelzoomPercent] = useState<number>(100);
  const wheelzoomPercentRef = useRef<number>(1);
  const wheelzoomInitPercentRef = useRef<number>();
  const [data, setData] = useState<V2UetEmtPreviewDashboardAuthorityPostResponse>();
  const breadcrumbRoutes = useBreadcrumbRoutes(); //面包屑

  useEffect(() => {
    if (energyId) {
      apiV2UetEmtPreviewDashboardAuthorityPost({ id: +energyId }).then(setData);
    }
  }, [energyId]);

  const size = useSize(ref);
  // 自适应dom的变化
  useEffect(() => {
    if (graph.current && size) {
      fitScreenView(graph.current, size.width, size.height);
      setWheelzoomPercent(100);
      wheelzoomInitPercentRef.current = graph.current.getZoom();
    }
  }, [size]);
  // 自适应屏幕缩放
  const handleResize = () => {
    if (ref.current && graph.current) {
      fitScreenView(graph.current, ref.current.clientWidth, ref.current.clientHeight);
      setWheelzoomPercent(100);
      wheelzoomInitPercentRef.current = graph.current.getZoom();
    }
  };

  // 自适应窗口变化
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatData = useMemo(() => {
    // 对返回数据进行处理
    if (data) {
      return formatNodesAndEdges(data, theme);
    }
  }, [data, theme]);

  useEffect(() => {
    if (formatData) {
      // 创建实例
      graph.current = new G6.Graph(basicConfig());
      // 读取数据
      graph.current.data(formatData);
      // 渲染图
      graph.current.render();
      // 绑定事件，打开抽屉
      graph.current.on('nodeselectchange', ev => {
        if ((ev.selectedItems as any).nodes.length) {
          showInfoByDrawer(ev.target, ev.select as boolean);
        } else {
          setOpen(false);
        }
      });
      graph.current.on('wheelzoom', ev => {
        // 返回值zoom表示当前视口的缩放比例
        const zoom = graph.current!.getZoom();
        if (wheelzoomInitPercentRef.current) {
          setWheelzoomPercent((zoom / wheelzoomInitPercentRef.current) * 100);
        }
      });
    }
  }, [formatData]);

  const showInfoByDrawer = (item: any, selected: boolean) => {
    if (item && item._cfg.id) {
      setItem(item); // 保存antv G6 的node实例
      // 通过过程id查询详细信息
      setItemDetailId(item._cfg.id);
      setOpen(selected);
    }
  };

  // 视图放大与缩小
  const onChangeZoom = (key: string) => {
    if (key === 'minus') {
      graph.current!.zoom(wheelzoomPercentRef.current - 0.2);
      setWheelzoomPercent(num => {
        return (num -= 20);
      });
      graph.current!.fitCenter();
    } else {
      graph.current!.zoom(wheelzoomPercentRef.current + 0.2);
      setWheelzoomPercent(num => {
        return (num += 20);
      });
      graph.current!.fitCenter();
    }
  };

  // 图例（接口返回几种类型就展示几种）
  const legendShowType = useMemo(() => {
    const typeList = (data?.nodes ?? []).map(item => item.info?.type as number);
    if ((typeList ?? []).length > 1) {
      return [...new Set(typeList)];
    }
    return [];
  }, [data]);

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]}>
      <FormTitle title="预览视图" />
      <div
        style={{
          width: '100%',
          height: 'calc(100Vh - 212px)',
          display: 'flex',
        }}
      >
        <div
          className={styles.left}
          ref={d => {
            if (d) {
              ref.current = d;
            }
          }}
        >
          <div className={styles.wheelzoomStyle}>
            <div className={styles.wheelzoomBtnStyle}>
              <Button type="text" icon={<MinusOutlined />} onClick={() => onChangeZoom('minus')}></Button>
            </div>
            <div style={{ width: 68, textAlign: 'center' }}>{wheelzoomPercent.toFixed(0)}%</div>
            <div className={styles.wheelzoomBtnStyle}>
              <Button type="text" icon={<PlusOutlined />} onClick={() => onChangeZoom('plus')}></Button>
            </div>
          </div>
          <Space className={styles.block}>
            {Object.entries(EnergyWorkingProcessTypeLabel)
              .filter(([value]) => legendShowType.includes(Number(value)))
              .map(([value, key]) => {
                const color = `${EnergyWorkingProcessTypeLabelColor[+value as EnergyWorkingProcessType]}`;
                return (
                  <div key={key} className={styles.item}>
                    <div
                      style={{
                        background: color,
                      }}
                      className={styles.pipe}
                    />
                    <div style={{ color: 'var(--mx-text-base-color)' }}>{key}</div>
                  </div>
                );
              })}
          </Space>
          <div className={styles.bgImg} id="mountNode"></div>
        </div>

        <div
          className={styles.right}
          style={{ width: open ? 400 : 0, height: '100%', backgroundColor: 'var(--mx-module-bg-color)' }}
        >
          <Drawer
            className={styles.drawerStyle}
            title="过程详情"
            placement="right"
            width={400}
            mask={false}
            onClose={() => {
              graph.current?.setItemState(item, 'selected', false);
              setOpen(false);
            }}
            open={open}
          >
            <ItemProcessDetail itemDetailId={itemDetailId} />
          </Drawer>
        </div>
      </div>
    </Wrapper>
  );
};

export default ViewEnergyWorkingTopo;
